import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  Box,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { ReactSortable } from "react-sortablejs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { hostApiClient } from "../hostApiClient";
import { getMediaTypeFromUrl, getMediaTypeFromFile, compressImages } from "../utils/mediaUtils";

interface UploadedMedia {
  id: string;
  src: string; // Final uploaded URL
  name: string;
  progress: number;
  uploading: boolean;
  compressing?: boolean; // Add compression state
  error?: boolean; // Add error state
  type: 'image' | 'video'; // Media type
}

interface MediaUploaderProps {
  initialMedia?: string[]; // Array of URLs
  label?: string; // Configurable label for the upload section
  onMediaChange?: (mediaUrls: string[]) => void; // Callback to notify media URLs change
  maxFiles?: number; // Maximum number of files allowed
  /** Thumbnail strip without filenames — for portfolio / gallery editors. */
  compact?: boolean;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  initialMedia = [],
  label = "Upload Media",
  onMediaChange,
  maxFiles,
  compact = false,
}) => {
  const theme = useTheme();
  const thumbSize = compact ? 96 : 140;
  const cardWidth = compact ? 96 : 180;

  const [uploadedMedia, setUploadedMedia] = useState<UploadedMedia[]>(() => {
    return initialMedia.map((src, index) => ({
      id: `existing-${index}`,
      src,
      name: src.split("/").pop() || `Media ${index + 1}`,
      progress: 100,
      uploading: false,
      error: false,
      type: getMediaTypeFromUrl(src),
    }));
  });

  // Ref to keep track of previous uploaded media to avoid redundant updates
  const prevUploadedMediaRef = useRef(uploadedMedia);



  useEffect(() => {
    console.log('MediaUploader: initialMedia changed:', initialMedia);
    // Update uploadedMedia when initialMedia prop changes
    const newUploadedMedia = initialMedia.map((src, index) => ({
      id: `existing-${index}`,
      src,
      name: src.split("/").pop() || `Media ${index + 1}`,
      progress: 100,
      uploading: false,
      error: false,
      type: getMediaTypeFromUrl(src),
    }));
    setUploadedMedia(newUploadedMedia);
  }, [initialMedia]);

  useEffect(() => {
    // Compare the new and previous media array to avoid unnecessary callback calls
    const currentMediaUrls = uploadedMedia.map((media) => media.src).filter(src => src !== '');
    const prevMediaUrls = prevUploadedMediaRef.current.map((media) => media.src).filter(src => src !== '');

    if (JSON.stringify(currentMediaUrls) !== JSON.stringify(prevMediaUrls)) {
      if (onMediaChange) {
        onMediaChange(currentMediaUrls);
      }
      prevUploadedMediaRef.current = uploadedMedia;
    }
  }, [uploadedMedia, onMediaChange]);

  const handleMediaError = (mediaId: string) => {
    console.error('MediaUploader: media failed to load for ID:', mediaId);
    setUploadedMedia((prev) =>
      prev.map((media) =>
        media.id === mediaId ? { ...media, error: true } : media
      )
    );
  };

  const handleMediaLoad = (mediaId: string) => {
    console.log('MediaUploader: media loaded successfully for ID:', mediaId);
    setUploadedMedia((prev) =>
      prev.map((media) =>
        media.id === mediaId ? { ...media, error: false } : media
      )
    );
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    let files = Array.from(event.target.files);
    
    // If maxFiles is set, limit the number of files
    if (maxFiles !== undefined) {
      const remainingSlots = maxFiles - uploadedMedia.length;
      if (remainingSlots <= 0) return;
      files = files.slice(0, remainingSlots);
    }

    const newMedia: UploadedMedia[] = files.map((file) => ({
      id: crypto.randomUUID(),
      src: "",
      name: file.name,
      progress: 0,
      uploading: true,
      compressing: getMediaTypeFromFile(file) === 'image', // Only compress images
      type: getMediaTypeFromFile(file),
    }));

    setUploadedMedia((prev) => [...prev, ...newMedia]);

    try {
      // Compress images before uploading
      const compressedFiles = await compressImages(files, 1920, 1080, 0.8);
      
      // Update compression status
      setUploadedMedia((prev) =>
        prev.map((media) =>
          newMedia.some(m => m.id === media.id) ? { ...media, compressing: false } : media
        )
      );
      
      const extensions = compressedFiles.map((f) => f.name.split(".").pop() || "jpg");
      const generateUploadUrlsResponse = await hostApiClient.generateUploadUrls({
        fileExtensions: extensions,
      });

      for (let i = 0; i < compressedFiles.length; i++) {
        const file = compressedFiles[i];
        const mediaId = newMedia[i].id;
        const url = generateUploadUrlsResponse.uploadUrls[i];

        try {
          await hostApiClient.uploadToPresignedUrl(url.uploadTo, file, (progress) => {
            setUploadedMedia((prev) =>
              prev.map((media) =>
                media.id === mediaId ? { ...media, progress, uploading: progress < 100 } : media
              )
            );
          });

          setUploadedMedia((prev) =>
            prev.map((media) =>
              media.id === mediaId
                ? { ...media, src: url.accessAt, uploading: false }
                : media
            )
          );
        } catch (e) {
          console.error("Upload failed for", file.name, e);
          setUploadedMedia((prev) =>
            prev.map((media) =>
              media.id === mediaId ? { ...media, uploading: false } : media
            )
          );
        }
      }
    } catch (error) {
      console.error("Failed to compress images:", error);
      // If compression fails, try uploading original files
      const extensions = files.map((f) => f.name.split(".").pop() || "jpg");
      const generateUploadUrlsResponse = await hostApiClient.generateUploadUrls({
        fileExtensions: extensions,
      });

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const mediaId = newMedia[i].id;
        const url = generateUploadUrlsResponse.uploadUrls[i];

        try {
          await hostApiClient.uploadToPresignedUrl(url.uploadTo, file, (progress) => {
            setUploadedMedia((prev) =>
              prev.map((media) =>
                media.id === mediaId ? { ...media, progress, uploading: progress < 100 } : media
              )
            );
          });

          setUploadedMedia((prev) =>
            prev.map((media) =>
              media.id === mediaId
                ? { ...media, src: url.accessAt, uploading: false }
                : media
            )
          );
        } catch (e) {
          console.error("Upload failed for", file.name, e);
          setUploadedMedia((prev) =>
            prev.map((media) =>
              media.id === mediaId ? { ...media, uploading: false } : media
            )
          );
        }
      }
    }
  };

  const handleRemoveMedia = (id: string) => {
    setUploadedMedia((prev) => {
      const filtered = prev.filter((media) => media.id !== id);
      return filtered;
    });
  };

  return (
    <div>
      {/* Header row: label on the left, upload on the right */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: compact ? "10px" : "16px",
        }}
      >
        <Typography variant={compact ? "subtitle1" : "h6"} fontWeight={compact ? 600 : undefined}>
          {label}
        </Typography>

        {/* Upload section on the right */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {!compact ? <Typography variant="body2">Upload new</Typography> : null}

          <IconButton
            component="label"
            color="primary"
            disabled={maxFiles !== undefined && uploadedMedia.length >= maxFiles}
            aria-label="Upload new"
            sx={{
              border: "1px solid",
              borderColor: theme.palette.primary.main,
              borderRadius: "8px",
              padding: compact ? "4px" : "4px 8px",
              height: "32px",
              minWidth: compact ? "32px" : undefined,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
          >
            <CloudUploadIcon fontSize="small" />
            <input
              type="file"
              accept="image/*,video/*"
              multiple={maxFiles !== 1}
              hidden
              onChange={handleFileSelect}
              disabled={maxFiles !== undefined && uploadedMedia.length >= maxFiles}
            />
          </IconButton>
        </div>
      </div>

      <ReactSortable
        list={uploadedMedia}
        setList={setUploadedMedia}
        animation={200}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: compact ? "10px" : "16px",
        }}
      >
        {uploadedMedia.map((media, index) => (
          <Card
            key={media.id}
            sx={{
              width: cardWidth,
              position: "relative",
              overflow: "hidden",
              borderRadius: compact ? 1.5 : undefined,
              border: index === 0
                ? `${compact ? 2 : 3}px solid ${theme.palette.primary.main}`
                : compact
                  ? `1px solid ${theme.palette.divider}`
                  : "none",
              boxShadow: index === 0 && !compact ? `0 0 10px ${theme.palette.primary.main}` : "none",
              transition: "border 0.3s ease, box-shadow 0.3s ease",
            }}
          >
            {media.src ? (
              media.type === 'video' ? (
                <Box sx={{ position: 'relative', height: thumbSize, width: '100%' }}>
                  <video
                    src={media.src}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={() => handleMediaError(media.id)}
                    onLoadedData={() => handleMediaLoad(media.id)}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '50%',
                      width: compact ? 28 : 40,
                      height: compact ? 28 : 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayArrowIcon sx={{ color: 'white', fontSize: compact ? 18 : 24 }} />
                  </Box>
                </Box>
              ) : (
                <CardMedia 
                  component="img" 
                  height={thumbSize}
                  image={media.src} 
                  alt={media.name}
                  onError={() => handleMediaError(media.id)}
                  onLoad={() => handleMediaLoad(media.id)}
                  sx={{
                    objectFit: 'cover',
                    backgroundColor: media.error ? theme.palette.error.light : 'transparent',
                  }}
                />
              )
            ) : (
              <Box
                sx={{
                  height: thumbSize,
                  backgroundColor: theme.palette.background.paper,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  …
                </Typography>
              </Box>
            )}
            
            {media.error && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                <Typography variant="caption" color="error">
                  Failed to load
                </Typography>
              </Box>
            )}
            
            {!compact ? (
              <CardContent>
                <Typography variant="body2" color="textSecondary">
                  {media.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {media.type === 'video' ? 'Video' : 'Image'}
                </Typography>
              </CardContent>
            ) : null}

            {media.compressing && (
              <Box
                sx={{
                  position: "absolute",
                  top: `calc(50% - ${compact ? 12 : 20}px)`,
                  left: `calc(50% - ${compact ? 12 : 20}px)`,
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <CircularProgress size={compact ? 24 : 40} />
                {!compact ? (
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px' }}>
                    Compressing...
                  </Typography>
                ) : null}
              </Box>
            )}
            
            {media.uploading && !media.compressing && (
              <CircularProgress
                size={compact ? 24 : 40}
                variant="determinate"
                value={media.progress}
                sx={{
                  position: "absolute",
                  top: `calc(50% - ${compact ? 12 : 20}px)`,
                  left: `calc(50% - ${compact ? 12 : 20}px)`,
                  zIndex: 1,
                }}
              />
            )}

            <IconButton
              color="secondary"
              size="small"
              onClick={() => handleRemoveMedia(media.id)}
              aria-label="Remove media"
              sx={{
                position: "absolute",
                top: compact ? 2 : 8,
                right: compact ? 2 : 8,
                zIndex: 2,
                bgcolor: compact ? "rgba(0,0,0,0.45)" : undefined,
                color: compact ? "common.white" : undefined,
                p: compact ? 0.25 : undefined,
                "&:hover": compact
                  ? { bgcolor: "rgba(0,0,0,0.65)" }
                  : undefined,
              }}
            >
              <Delete fontSize={compact ? "small" : "medium"} />
            </IconButton>
          </Card>
        ))}
      </ReactSortable>
    </div>
  );
};

export default MediaUploader;
